/**
 * KW Customs 360 product viewer.
 * Self-hosted, vanilla JS. Handles:
 *  - mouse drag, touch drag, scroll wheel to rotate
 *  - optional auto-rotate until first user interaction
 *  - frame preloading with progress indicator
 *
 * Usage: decorate a container with class "kw-360" and a data-frames
 *   attribute containing a JSON array of frame URLs. Optionally set
 *   data-auto-rotate="true" and data-rotate-speed="50" (ms per frame).
 *
 * Example:
 *   <div class="kw-360"
 *        data-frames='["/path/frame-01.webp","/path/frame-02.webp"]'
 *        data-auto-rotate="true">
 *     <img class="kw-360__image" alt="...">
 *   </div>
 */
(function () {
  function initViewer(container) {
    if (container.dataset.kw360Init === '1') return;
    container.dataset.kw360Init = '1';

    var frames;
    try {
      frames = JSON.parse(container.dataset.frames || '[]');
    } catch (e) {
      console.warn('[kw-360] invalid data-frames JSON', e);
      return;
    }
    if (!frames.length) return;

    var image = container.querySelector('.kw-360__image');
    if (!image) {
      image = document.createElement('img');
      image.className = 'kw-360__image';
      container.appendChild(image);
    }

    var progress = document.createElement('div');
    progress.className = 'kw-360__loading';
    progress.innerHTML = '<div class="kw-360__spinner"></div><span class="kw-360__loading-text">Loading 0%</span>';
    container.appendChild(progress);

    var hint = document.createElement('div');
    hint.className = 'kw-360__hint';
    hint.innerHTML = '<i class="fas fa-sync-alt"></i><span>Click and drag to rotate</span>';
    container.appendChild(hint);

    var currentFrame = 0;
    var preloaded = [];
    var loadedCount = 0;
    var isDragging = false;
    var lastX = 0;
    var autoRotate = container.dataset.autoRotate === 'true';
    var rotateSpeed = parseInt(container.dataset.rotateSpeed, 10) || 90;
    var pixelsPerFrame = parseInt(container.dataset.pixelsPerFrame, 10) || 8;
    var autoRotateTimer = null;
    var userInteracted = false;

    function setFrame(idx) {
      var total = frames.length;
      currentFrame = ((idx % total) + total) % total;
      image.src = frames[currentFrame];
    }

    function preloadFrame(i) {
      return new Promise(function (resolve) {
        if (preloaded[i]) return resolve();
        var img = new Image();
        img.onload = img.onerror = function () {
          preloaded[i] = true;
          loadedCount += 1;
          var pct = Math.round((loadedCount / frames.length) * 100);
          var text = progress.querySelector('.kw-360__loading-text');
          if (text) text.textContent = 'Loading ' + pct + '%';
          resolve();
        };
        img.src = frames[i];
      });
    }

    function preloadAll() {
      // First frame: prioritize so we can show something immediately
      preloadFrame(0).then(function () {
        setFrame(0);
        container.classList.add('kw-360--first-loaded');
      });
      // Rest: kick off eagerly in parallel
      var rest = [];
      for (var i = 1; i < frames.length; i++) rest.push(preloadFrame(i));
      Promise.all(rest).then(function () {
        container.classList.add('kw-360--ready');
        progress.classList.add('kw-360__loading--done');
        if (autoRotate) startAutoRotate();
      });
    }

    function startAutoRotate() {
      if (autoRotateTimer) return;
      autoRotateTimer = setInterval(function () {
        if (userInteracted) return;
        setFrame(currentFrame + 1);
      }, rotateSpeed);
    }

    function stopAutoRotate() {
      userInteracted = true;
      if (autoRotateTimer) {
        clearInterval(autoRotateTimer);
        autoRotateTimer = null;
      }
      container.classList.add('kw-360--user-controlled');
    }

    function onPointerDown(e) {
      isDragging = true;
      lastX = e.clientX != null ? e.clientX : (e.touches && e.touches[0].clientX);
      stopAutoRotate();
      container.classList.add('kw-360--dragging');
      e.preventDefault();
    }

    function onPointerMove(e) {
      if (!isDragging) return;
      var x = e.clientX != null ? e.clientX : (e.touches && e.touches[0].clientX);
      if (x == null) return;
      var delta = x - lastX;
      if (Math.abs(delta) < pixelsPerFrame) return;
      var framesShift = Math.trunc(delta / pixelsPerFrame);
      setFrame(currentFrame + framesShift);
      lastX = x;
      e.preventDefault();
    }

    function onPointerUp() {
      isDragging = false;
      container.classList.remove('kw-360--dragging');
    }

    function onWheel(e) {
      // Only intercept when the user is interacting directly (not just scrolling past).
      // Require horizontal scroll OR shift-key + vertical. Otherwise let the page scroll.
      var horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (!horizontal && !e.shiftKey) return;
      var d = horizontal ? e.deltaX : e.deltaY;
      setFrame(currentFrame + (d > 0 ? 1 : -1));
      stopAutoRotate();
      e.preventDefault();
    }

    // Mouse
    container.addEventListener('mousedown', onPointerDown);
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', onPointerUp);

    // Touch
    container.addEventListener('touchstart', onPointerDown, { passive: false });
    container.addEventListener('touchmove', onPointerMove, { passive: false });
    container.addEventListener('touchend', onPointerUp);

    // Wheel
    container.addEventListener('wheel', onWheel, { passive: false });

    // Prevent native image drag
    image.addEventListener('dragstart', function (e) { e.preventDefault(); });

    preloadAll();
  }

  function scan() {
    var containers = document.querySelectorAll('.kw-360');
    for (var i = 0; i < containers.length; i++) initViewer(containers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Re-scan when the Shopify theme editor swaps sections
  document.addEventListener('shopify:section:load', scan);
})();
